import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IonSpinner,
  IonText,
  IonRefresher,
  IonRefresherContent,
  IonIcon,
  RefresherEventDetail,
} from '@ionic/react';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import {
  getMarketDataSales,
  getMarketDataEvents,
  getMarketDataFixtures,
  getFixtureSales,
  getTeamSales,
  getLatestSales,
  MarketDataSale,
  MarketDataEvent,
  MarketDataFixture,
} from '../services/marketDataService';
import { format } from 'date-fns';

const ALL = '__ALL__';

// Group sales by a STABLE fixture identity (event name + calendar day), never by the
// eventId token — that token is dynamic and the same fixture can carry several over time,
// which would otherwise show one match as multiple events.
const dayOf = (d?: string | null) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const fixtureKey = (eventName?: string | null, showDate?: string | null) =>
  `${eventName ?? ''}|${dayOf(showDate)}`;

// Home team = the part before "vs" in the event name (the name is the first half of the fixtureKey).
const homeTeamOfKey = (key: string) =>
  (key.split('|')[0] || '').split(/\s+vs\.?\s+/i)[0].trim();

const selectClass =
  'w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:border-primary-500';
const inputClass =
  'w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:border-primary-500';
const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

function money(n: number): string {
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Money with the sale's own currency symbol (World Cup sales are in USD, the rest GBP).
function moneyCur(n: number, currency?: string | null): string {
  const sym = currency === 'USD' ? '$' : '£';
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Short "live" relative time, e.g. "just now", "4m ago", "3h ago", "2d ago".
function relativeTime(iso: string | null | undefined, now: number): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const m = Math.floor(Math.max(0, now - t) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Which marketplace's sales the whole screen looks at. Kept SEPARATE by default — Live and StubHub
// price differently, so mixing them silently would skew every average — with 'both' as the opt-in
// joined view (fixture names are normalised at gather time, so the same match merges cleanly).
type DataSource = 'live' | 'stubhub' | 'both';
const isStubhubSale = (s: MarketDataSale) => s.source === 'stubhub';

const MarketDataContent: React.FC = () => {
  // Sales of the CURRENT selection only (one game, a team's games, or everything for "All events"),
  // fetched on demand. The screen no longer downloads every sale on open.
  const [allSales, setAllSales] = useState<MarketDataSale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [fixtures, setFixtures] = useState<MarketDataFixture[]>([]);
  const [latestRaw, setLatestRaw] = useState<MarketDataSale[]>([]);
  const salesCache = useRef(new Map<string, MarketDataSale[]>());
  const [reloadTick, setReloadTick] = useState(0);
  // Set only when the server predates the on-demand endpoints: then every sale is loaded once and
  // selections are filtered locally, exactly as the screen used to work.
  const legacyAll = useRef<MarketDataSale[] | null>(null);
  const [dataSource, setDataSource] = useState<DataSource>('live');
  // Everything below works off this filtered view, so the event list, latest feed, filters and
  // aggregates all follow the toggle with no further changes.
  const sales = useMemo(
    () =>
      dataSource === 'both'
        ? allSales
        : allSales.filter((s) => isStubhubSale(s) === (dataSource === 'stubhub')),
    [allSales, dataSource],
  );
  const [events, setEvents] = useState<MarketDataEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Event selector
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>(''); // '' = all home teams
  const [eventQuery, setEventQuery] = useState(''); // search box text
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [latestOpen, setLatestOpen] = useState(true); // minimize/maximize the latest-sales feed

  // Ticks every 30s so the "latest sales" relative times stay live.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Scroll target: clicking a latest sale jumps the per-event breakdown into view.
  const detailRef = useRef<HTMLDivElement>(null);
  const jumpToEvent = (s: MarketDataSale) => {
    setTeamFilter('');
    setEventQuery('');
    setSelectedEvent(fixtureKey(s.eventName, s.showDate));
    clearFilters();
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Filters
  const [fCategory, setFCategory] = useState('');
  const [fSection, setFSection] = useState('');
  const [fAllocation, setFAllocation] = useState('');
  const [fSplit, setFSplit] = useState('');
  const [fSeating, setFSeating] = useState('');
  const [fBroker, setFBroker] = useState(''); // '' | 'mine' | 'market'
  const [fMinPrice, setFMinPrice] = useState('');
  const [fMaxPrice, setFMaxPrice] = useState('');
  const [fMinQty, setFMinQty] = useState('');
  const [fMaxQty, setFMaxQty] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');

  const load = async () => {
    try {
      setError(null);
      setLoading(true);
      let fixturesData: MarketDataFixture[];
      let latestData: MarketDataSale[];
      let eventsData: MarketDataEvent[];
      try {
        [fixturesData, eventsData, latestData] = await Promise.all([
          getMarketDataFixtures(),
          getMarketDataEvents(),
          getLatestSales(60),
        ]);
        legacyAll.current = null;
      } catch (e: any) {
        if (e?.response?.status !== 404) throw e;
        // Older server: load everything once and derive the game list and feed from it.
        const [all, evs] = await Promise.all([getMarketDataSales(), getMarketDataEvents()]);
        legacyAll.current = all;
        eventsData = evs;
        const byKey = new Map<string, MarketDataFixture>();
        for (const r of all) {
          if (!r.eventName || !r.showDate) continue;
          const day = dayOf(r.showDate);
          const k = `${r.eventName}|${day}`;
          const f = byKey.get(k) || { eventName: r.eventName, day, showDate: r.showDate, live: 0, stubhub: 0, last: null };
          if (isStubhubSale(r)) f.stubhub++; else f.live++;
          if (!f.last || r.createdOn > f.last) f.last = r.createdOn;
          byKey.set(k, f);
        }
        fixturesData = [...byKey.values()];
        latestData = all.slice(0, 600);
      }
      salesCache.current.clear();
      setReloadTick((t) => t + 1);
      setFixtures(fixturesData);
      setEvents(eventsData);
      setLatestRaw(latestData);
      // Default to the game of the newest sale in the current marketplace.
      const newest = latestData.find((s) => dataSource === 'both' || isStubhubSale(s) === (dataSource === 'stubhub'));
      const firstEvent = newest ? fixtureKey(newest.eventName, newest.showDate) : '';
      setSelectedEvent((prev) => prev || firstEvent);
    } catch (err: any) {
      setError(err.message || 'Failed to load market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await load();
    e.detail.complete();
  };

  const clearFilters = () => {
    setFCategory(''); setFSection(''); setFAllocation(''); setFSplit(''); setFSeating('');
    setFBroker(''); setFMinPrice(''); setFMaxPrice(''); setFMinQty('');
    setFMaxQty(''); setFDateFrom(''); setFDateTo('');
  };

  // fixtureKey -> { label, showDate } map, built from the denormalised name/date on the
  // sales (and events as a fallback). Keyed on the stable identity, NOT the eventId token.
  const eventInfo = useMemo(() => {
    const map = new Map<string, { label: string; showDate: string | null }>();
    const mk = (name: string, date: string | null) => {
      const datePart = date ? format(new Date(date), 'dd MMM yyyy') : '';
      return { label: datePart ? `${name} — ${datePart}` : name, showDate: date };
    };
    for (const f of fixtures) {
      const k = fixtureKey(f.eventName, f.showDate);
      if (f.eventName && !map.has(k)) map.set(k, mk(f.eventName, f.showDate ?? null));
    }
    for (const ev of events) {
      const k = fixtureKey(ev.eventName, ev.showDate);
      if (ev.eventName && !map.has(k)) map.set(k, mk(ev.eventName, ev.showDate));
    }
    return map;
  }, [fixtures, events]);

  const eventLabel = (key: string) => eventInfo.get(key)?.label ?? key;

  // Several games in the table at once ("All events" / "All <team> games"): show which game each
  // sale was for. With a team chosen the home side is implied, so show just the opponent and date.
  const multiGame = selectedEvent === ALL;
  const gameShort = (s: MarketDataSale) => {
    const name = s.eventName ?? '';
    const [home, away] = name.split(/\s+vs\.?\s+/i);
    const who = teamFilter && away && home?.trim() === teamFilter ? `v ${away.trim()}` : name;
    return s.showDate ? `${who} · ${format(new Date(s.showDate), 'dd MMM yy')}` : who;
  };

  // Fixtures that have sales in the chosen marketplace (by name + day), sorted by show date then label.
  // Built from the fixture list, so no sales need to be downloaded to list the games.
  const eventKeys = useMemo(() => {
    const has = (f: MarketDataFixture) =>
      dataSource === 'both' ? f.live + f.stubhub > 0 : dataSource === 'stubhub' ? f.stubhub > 0 : f.live > 0;
    const keys = [...new Set(fixtures.filter(has).map((f) => fixtureKey(f.eventName, f.showDate)))]
      .filter((k) => k && k !== '|');
    return keys.sort((a, b) => {
      const da = eventInfo.get(a)?.showDate;
      const db = eventInfo.get(b)?.showDate;
      if (da && db) return new Date(da).getTime() - new Date(db).getTime();
      if (da) return -1;
      if (db) return 1;
      return eventLabel(a).localeCompare(eventLabel(b));
    });
  }, [fixtures, dataSource, eventInfo]);

  // Switching marketplace can strand the selection on a fixture the new source has no sales for —
  // snap to that source's first fixture instead of showing an empty breakdown.
  useEffect(() => {
    if (selectedEvent && selectedEvent !== ALL && !eventKeys.includes(selectedEvent)) {
      setSelectedEvent(eventKeys[0] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, eventKeys]);

  // Distinct home teams present in the sales, for the "Home team" dropdown.
  const homeTeams = useMemo(
    () => [...new Set(eventKeys.map(homeTeamOfKey).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [eventKeys],
  );

  // The event list narrowed to the chosen home team (the scrollable list of that team's games).
  const teamEventKeys = useMemo(
    () => eventKeys.filter((k) => !teamFilter || homeTeamOfKey(k) === teamFilter),
    [eventKeys, teamFilter],
  );

  // Autocomplete suggestions for the search box — matches within the current team filter.
  const suggestions = useMemo(() => {
    const q = eventQuery.trim().toLowerCase();
    if (!q) return [];
    return teamEventKeys.filter((k) => eventLabel(k).toLowerCase().includes(q)).slice(0, 20);
  }, [teamEventKeys, eventQuery, eventInfo]);

  // Sales for the selected fixture (before field filters) — drives the filter dropdown options
  const eventSales = useMemo(
    () =>
      selectedEvent === ALL
        ? sales
        : sales.filter((s) => fixtureKey(s.eventName, s.showDate) === selectedEvent),
    [sales, selectedEvent],
  );

  // Fetch the selection's sales when the game (or, for "All events", the team) changes. Cached for
  // the session so flipping back to a game is instant; Refresh clears the cache.
  useEffect(() => {
    if (!selectedEvent) return;
    const key = selectedEvent === ALL ? `ALL|${teamFilter}` : selectedEvent;
    const hit = salesCache.current.get(key);
    if (hit) { setAllSales(hit); return; }
    if (legacyAll.current) {
      const all = legacyAll.current;
      const rows = selectedEvent !== ALL
        ? all.filter((s) => fixtureKey(s.eventName, s.showDate) === selectedEvent)
        : teamFilter
          ? all.filter((s) => homeTeamOfKey(fixtureKey(s.eventName, s.showDate)) === teamFilter)
          : all;
      setAllSales(rows);
      return;
    }
    let cancelled = false;
    setSalesLoading(true);
    const [name, day] = selectedEvent.split('|');
    const p = selectedEvent !== ALL
      ? getFixtureSales(name, day)
      : teamFilter
        ? getTeamSales(teamFilter)
        : getMarketDataSales();
    p.then((rows) => {
      salesCache.current.set(key, rows);
      if (!cancelled) setAllSales(rows);
    })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load sales'); })
      .finally(() => { if (!cancelled) setSalesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEvent, teamFilter, reloadTick]);

  const distinct = (key: keyof MarketDataSale) =>
    [...new Set(eventSales.map((s) => s[key]).filter((v) => v != null && v !== ''))]
      .map(String)
      .sort();

  const categories = useMemo(() => distinct('category'), [eventSales]);
  const sections = useMemo(() => distinct('section'), [eventSales]);
  const allocations = useMemo(() => distinct('allocation'), [eventSales]);
  const splits = useMemo(() => distinct('splitAs'), [eventSales]);
  const seatings = useMemo(() => distinct('seatingArrangement'), [eventSales]);

  // Apply all field filters
  const filtered = useMemo(() => {
    return eventSales.filter((s) => {
      if (fCategory && s.category !== fCategory) return false;
      if (fSection && s.section !== fSection) return false;
      if (fAllocation && s.allocation !== fAllocation) return false;
      if (fSplit && s.splitAs !== fSplit) return false;
      if (fSeating && s.seatingArrangement !== fSeating) return false;
      if (fBroker === 'mine' && !s.currentBroker) return false;
      if (fBroker === 'market' && s.currentBroker) return false;
      if (fMinPrice !== '' && s.sellAmount < Number(fMinPrice)) return false;
      if (fMaxPrice !== '' && s.sellAmount > Number(fMaxPrice)) return false;
      if (fMinQty !== '' && s.quantity < Number(fMinQty)) return false;
      if (fMaxQty !== '' && s.quantity > Number(fMaxQty)) return false;
      if (fDateFrom && new Date(s.createdOn) < new Date(fDateFrom)) return false;
      if (fDateTo && new Date(s.createdOn) > new Date(`${fDateTo}T23:59:59`)) return false;
      return true;
    });
  }, [eventSales, fCategory, fSection, fAllocation, fSplit, fSeating, fBroker, fMinPrice, fMaxPrice, fMinQty, fMaxQty, fDateFrom, fDateTo]);

  // Latest 30 sales across ALL games (independent of the selected event / filters),
  // newest-first. Sorted defensively in case the API order ever changes.
  //
  // Dedup twin rows: the same real sale can be stored under two event docs for one fixture (a
  // handful of fixtures exist twice in livefootballevents on adjacent days), producing rows that
  // differ only by showDate/eventId/saleKey. Collapse by the sale's intrinsic identity (name +
  // section + split + price + qty + exact sale time) so each sale shows once. createdOn is kept in
  // the key so two genuinely distinct sales of the same seats at different times aren't merged.
  const latestSales = useMemo(() => {
    const pool = dataSource === 'both'
      ? latestRaw
      : latestRaw.filter((s) => isStubhubSale(s) === (dataSource === 'stubhub'));
    const sorted = [...pool].sort(
      (a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
    );
    const seen = new Set<string>();
    const out: MarketDataSale[] = [];
    for (const s of sorted) {
      const key = [
        s.eventName ?? '',
        s.category ?? '',
        s.seatingArrangement ?? '',
        s.allocation ?? '',
        s.splitAs ?? '',
        s.sellAmount ?? '',
        s.quantity ?? '',
        s.createdOn ? new Date(s.createdOn).getTime() : '',
      ].join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
      if (out.length >= 30) break;
    }
    return out;
  }, [latestRaw, dataSource]);

  // Aggregates over the filtered set (sellAmount is PER TICKET)
  const agg = useMemo(() => {
    const totalSales = filtered.length;
    const totalTickets = filtered.reduce((a, s) => a + (s.quantity || 0), 0);
    const totalRevenue = filtered.reduce((a, s) => a + (s.sellAmount || 0) * (s.quantity || 0), 0);
    const avgPerTicket = totalTickets ? totalRevenue / totalTickets : 0;
    const prices = filtered.map((s) => s.sellAmount).filter((n): n is number => n != null);
    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;
    return { totalSales, totalTickets, totalRevenue, avgPerTicket, minPrice, maxPrice };
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <IonSpinner name="crescent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-8 text-center">
        <IonText color="danger"><p>{error}</p></IonText>
      </div>
    );
  }

  const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="p-4 space-y-4">
        {/* Marketplace toggle — Live and StubHub stay separate (they price differently, so mixed
            averages would mislead); Both is the opt-in joined view. */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Marketplace</span>
          {(['live', 'stubhub', 'both'] as DataSource[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setDataSource(m)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                dataSource === m
                  ? m === 'stubhub'
                    ? 'bg-violet-600 border-violet-600 text-white'
                    : 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {m === 'live' ? 'Live' : m === 'stubhub' ? 'StubHub' : 'Both'}
            </button>
          ))}
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {fixtures
              .reduce((a, f) => a + (dataSource === 'both' ? f.live + f.stubhub : dataSource === 'stubhub' ? f.stubhub : f.live), 0)
              .toLocaleString()}{' '}
            sales stored
          </span>
        </div>

        {/* Latest sales across all games — a live activity feed; your own sales are green */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg">
          <div
            onClick={() => setLatestOpen((o) => !o)}
            className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800/50"
            title={latestOpen ? 'Minimize' : 'Show latest sales'}
          >
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Latest sales</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">30 most recent · all games</p>
            </div>
            <div className="flex items-center gap-3">
              {latestOpen && (
                <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green-500" /> yours
                </span>
              )}
              <IonIcon
                icon={latestOpen ? chevronUpOutline : chevronDownOutline}
                className="text-gray-400 dark:text-gray-500 text-lg"
              />
            </div>
          </div>
          {latestOpen && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 max-h-96 overflow-y-auto">
            {latestSales.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">No sales yet.</li>
            ) : (
              latestSales.map((s) => (
                <li
                  key={s._id}
                  onClick={() => jumpToEvent(s)}
                  title="Jump to this event's breakdown"
                  className={`flex items-center gap-3 px-3 py-2 border-l-4 cursor-pointer transition-colors ${
                    s.currentBroker
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-500 hover:bg-green-100 dark:hover:bg-green-900/30'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {/* Price + quantity lead the row, kept tight together */}
                  <div className="flex-shrink-0 flex items-center gap-1.5">
                    <span className={`w-16 text-right text-base font-bold tabular-nums ${s.currentBroker ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'}`}>
                      {moneyCur(s.sellAmount, s.currency)}
                    </span>
                    <span className="px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-bold tabular-nums">
                      ×{s.quantity}
                    </span>
                  </div>
                  {/* Event info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {s.eventName ?? '—'}
                      </span>
                      {s.currentBroker && (
                        <span className="flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-green-600 text-white">
                          You
                        </span>
                      )}
                      {/* Marketplace tag, so the joined view stays legible */}
                      {dataSource === 'both' && (
                        <span className={`flex-shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          isStubhubSale(s) ? 'bg-violet-600 text-white' : 'bg-blue-600/80 text-white'
                        }`}>
                          {isStubhubSale(s) ? 'SH' : 'Live'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {[[s.category, s.section].filter(Boolean).join(' '), s.allocation, s.seatingArrangement].filter(Boolean).join(' · ')}
                      {s.showDate ? ` · ${format(new Date(s.showDate), 'dd MMM')}` : ''}
                      {' · '}
                      <span title={s.createdOn ? format(new Date(s.createdOn), 'dd MMM yyyy HH:mm') : undefined}>
                        {relativeTime(s.createdOn, now)}
                      </span>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
          )}
        </div>

        {/* Find an event: by home team and/or by search */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Home team</label>
            <select
              className={selectClass}
              value={teamFilter}
              onChange={(e) => {
                const t = e.target.value;
                setTeamFilter(t);
                setEventQuery('');
                // Jump to the first of the team's games (or All when cleared)
                const list = eventKeys.filter((k) => !t || homeTeamOfKey(k) === t);
                setSelectedEvent(t ? (list[0] ?? ALL) : ALL);
                clearFilters();
              }}
            >
              <option value="">All teams</option>
              {homeTeams.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className={labelClass}>Search game</label>
            <input
              className={inputClass}
              value={eventQuery}
              placeholder="Type a team or match…"
              onChange={(e) => { setEventQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            />
            {showSuggestions && eventQuery.trim() !== '' && suggestions.length > 0 && (
              <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg">
                {suggestions.map((k) => (
                  <li key={k}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSelectedEvent(k); setEventQuery(''); setShowSuggestions(false); clearFilters(); }}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {eventLabel(k)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Event selector — scrolls through the chosen team's games */}
        <div ref={detailRef} style={{ scrollMarginTop: '12px' }}>
          <label className={labelClass}>Event{teamFilter ? ` · ${teamFilter} (${teamEventKeys.length})` : ''}</label>
          <select
            className={selectClass}
            value={selectedEvent}
            onChange={(e) => { setSelectedEvent(e.target.value); clearFilters(); }}
          >
            <option value={ALL}>{teamFilter ? `All ${teamFilter} games` : 'All events (loads every sale)'}</option>
            {teamEventKeys.map((id) => (
              <option key={id} value={id}>{eventLabel(id)}</option>
            ))}
          </select>
        </div>

        {/* Aggregates */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Sales" value={String(agg.totalSales)} />
          <StatCard label="Tickets sold" value={String(agg.totalTickets)} />
          <StatCard label="Total revenue" value={money(agg.totalRevenue)} />
          <StatCard label="Avg / ticket" value={money(agg.avgPerTicket)} />
          <StatCard label="Min price" value={money(agg.minPrice)} />
          <StatCard label="Max price" value={money(agg.maxPrice)} />
        </div>

        {/* Filter bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Filters</h3>
            <button
              onClick={clearFilters}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelClass}>Category</label>
              <select className={selectClass} value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                <option value="">All</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Section</label>
              <select className={selectClass} value={fSection} onChange={(e) => setFSection(e.target.value)}>
                <option value="">All</option>
                {sections.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Allocation</label>
              <select className={selectClass} value={fAllocation} onChange={(e) => setFAllocation(e.target.value)}>
                <option value="">All</option>
                {allocations.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Split</label>
              <select className={selectClass} value={fSplit} onChange={(e) => setFSplit(e.target.value)}>
                <option value="">All</option>
                {splits.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Seating</label>
              <select className={selectClass} value={fSeating} onChange={(e) => setFSeating(e.target.value)}>
                <option value="">All</option>
                {seatings.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Source</label>
              <select className={selectClass} value={fBroker} onChange={(e) => setFBroker(e.target.value)}>
                <option value="">All</option>
                <option value="mine">Mine</option>
                <option value="market">Market</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Price (per ticket)</label>
              <div className="flex gap-2">
                <input className={inputClass} type="number" placeholder="Min" value={fMinPrice} onChange={(e) => setFMinPrice(e.target.value)} />
                <input className={inputClass} type="number" placeholder="Max" value={fMaxPrice} onChange={(e) => setFMaxPrice(e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Quantity</label>
              <div className="flex gap-2">
                <input className={inputClass} type="number" placeholder="Min" value={fMinQty} onChange={(e) => setFMinQty(e.target.value)} />
                <input className={inputClass} type="number" placeholder="Max" value={fMaxQty} onChange={(e) => setFMaxQty(e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Sold date</label>
              <div className="flex gap-2">
                <input className={inputClass} type="date" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)} />
                <input className={inputClass} type="date" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Sales table */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sold</th>
                {multiGame && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Game</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Section</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Alloc</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Split</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Seating</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">£ / ticket</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Line total</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {salesLoading ? (
                <tr>
                  <td colSpan={multiGame ? 11 : 10} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                    <IonSpinner name="crescent" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={multiGame ? 11 : 10} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                    No sales match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s._id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {s.createdOn ? format(new Date(s.createdOn), 'dd MMM HH:mm') : '–'}
                    </td>
                    {multiGame && (
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => { setSelectedEvent(fixtureKey(s.eventName, s.showDate)); clearFilters(); }}
                          title={`${eventLabel(fixtureKey(s.eventName, s.showDate))} · open this game`}
                          className="text-left text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {gameShort(s)}
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{s.category}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{s.section}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{s.allocation}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{s.splitAs}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{s.seatingArrangement}</td>
                    <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-gray-100">{s.quantity}</td>
                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100">{money(s.sellAmount)}</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-300">{money((s.sellAmount || 0) * (s.quantity || 0))}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        s.currentBroker
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {s.currentBroker ? 'Mine' : 'Market'}
                      </span>
                      {dataSource === 'both' && (
                        <span className={`ml-1 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          isStubhubSale(s)
                            ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          {isStubhubSale(s) ? 'SH' : 'Live'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default MarketDataContent;
